import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';
import { useAuth } from '../../store/AuthContext';
import { Users, Shield, ShieldAlert, Loader2, RefreshCw, X, Save, Trash2, Key, UserPlus } from 'lucide-react';

const UserManagementPage = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', role: 'user', password: '' });
  const [creating, setCreating] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${getApiUrl()}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(resp.data.data.users);
      setError(null);
    } catch (err) {
      setError('FAILED TO RETRIEVE PERSONNEL ROSTER');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      password: ''
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;

      await axios.put(`${getApiUrl()}/auth/users/${selectedUser._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      alert('FAILED TO UPDATE PERSONNEL DATA');
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setCreateForm({ name: '', email: '', role: 'user', password: '' });
    setShowCreate(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${getApiUrl()}/auth/users`, createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreate(false);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'FAILED TO ENLIST PERSONNEL');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`TERMINATE MISSION FOR ${selectedUser.name.toUpperCase()}?`)) return;
    
    setSaving(true);
    try {
      await axios.delete(`${getApiUrl()}/auth/users/${selectedUser._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      alert('FAILED TO TERMINATE PERSONNEL');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <Users size={32} color="#0B3D91" />
          <h1 style={{ margin: 0, letterSpacing: '3px', textTransform: 'uppercase' }}>Mission Personnel</h1>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="nasa-button" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserPlus size={16} /> AÑADIR PERSONAL
          </button>
          <button className="nasa-button" onClick={fetchUsers} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RefreshCw size={16} /> REFRESH ROSTER
          </button>
        </div>
      </div>

      <div style={{ background: 'rgba(5, 7, 10, 0.9)', border: '1px solid #0B3D91', borderRadius: '4px', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '100px', textAlign: 'center' }}>
            <Loader2 className="animate-spin" size={40} color="#0B3D91" style={{ margin: '0 auto' }} />
            <p style={{ marginTop: '20px', letterSpacing: '2px', color: '#0B3D91' }}>QUERYING CENTRAL COMMAND DATABASE...</p>
          </div>
        ) : error ? (
          <div style={{ padding: '100px', textAlign: 'center', color: '#D63031' }}>
            <ShieldAlert size={40} style={{ margin: '0 auto' }} />
            <p style={{ marginTop: '20px' }}>{error}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '560px', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#0B3D91', color: 'white' }}>
                <th style={{ padding: '15px', fontSize: '0.7rem', letterSpacing: '1px' }}>NAME</th>
                <th style={{ padding: '15px', fontSize: '0.7rem', letterSpacing: '1px' }}>EMAIL / COMM CHANNEL</th>
                <th style={{ padding: '15px', fontSize: '0.7rem', letterSpacing: '1px' }}>SYSTEM ROLE</th>
                <th style={{ padding: '15px', fontSize: '0.7rem', letterSpacing: '1px' }}>CREATED</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr 
                  key={u._id} 
                  style={{ borderBottom: '1px solid #1A1A1A', cursor: 'pointer' }}
                  onClick={() => handleEditClick(u)}
                  onMouseOver={(e) => e.currentTarget.style.background = 'rgba(11, 61, 145, 0.1)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '15px', fontSize: '0.9rem', color: '#00CEC9' }}>{u.name}</td>
                  <td style={{ padding: '15px', fontSize: '0.9rem', color: '#AAA' }}>{u.email}</td>
                  <td style={{ padding: '15px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '5px', 
                      color: u.role === 'admin' ? '#00CEC9' : '#E27B58',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      <Shield size={14} />
                      {u.role.toUpperCase()}
                    </div>
                  </td>
                  <td style={{ padding: '15px', fontSize: '0.8rem', color: '#555' }}>
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* EDIT MODAL */}
      {selectedUser && (
        <div style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center',
          padding: '16px', overflowY: 'auto', zIndex: 1000, backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            width: '100%', maxWidth: '450px', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
            backgroundColor: '#05070A', border: '2px solid #0B3D91',
            borderRadius: '8px', padding: '30px', position: 'relative', boxShadow: '0 0 30px rgba(11, 61, 145, 0.3)'
          }}>
            <button 
              onClick={() => setSelectedUser(null)}
              style={{ position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', cursor: 'pointer', color: '#555' }}
            >
              <X size={20} />
            </button>

            <h2 style={{ letterSpacing: '2px', textTransform: 'uppercase', color: '#0B3D91', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Shield size={24} /> Personnel Modification
            </h2>

            <form onSubmit={handleSave}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#555', marginBottom: '5px' }}>FULL NAME</label>
                <input 
                  type="text" 
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  style={{ width: '100%', padding: '10px', background: '#111', border: '1px solid #333', color: 'white' }}
                  required
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#555', marginBottom: '5px' }}>EMAIL CHANNEL</label>
                <input 
                  type="email" 
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  style={{ width: '100%', padding: '10px', background: '#111', border: '1px solid #333', color: 'white' }}
                  required
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#555', marginBottom: '5px' }}>SECURITY ROLE</label>
                <select 
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  style={{ width: '100%', padding: '10px', background: '#111', border: '1px solid #333', color: 'white' }}
                >
                  <option value="user">USER (Standard Access)</option>
                  <option value="admin">ADMIN (Central Command)</option>
                </select>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#555', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Key size={12} /> OVERRIDE PASSWORD (Optional)
                </label>
                <input 
                  type="password" 
                  placeholder="Leave blank to maintain current"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  style={{ width: '100%', padding: '10px', background: '#111', border: '1px solid #333', color: 'white' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  type="submit" 
                  className="nasa-button" 
                  disabled={saving}
                  style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                >
                  <Save size={16} /> {saving ? 'SYNCING...' : 'SAVE CHANGES'}
                </button>
                <button 
                  type="button" 
                  onClick={handleDelete}
                  disabled={saving}
                  style={{ padding: '0 15px', background: 'rgba(214, 48, 49, 0.1)', border: '1px solid #D63031', color: '#D63031', cursor: 'pointer', borderRadius: '4px' }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CREATE MODAL */}
      {showCreate && (
        <div style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center',
          padding: '16px', overflowY: 'auto', zIndex: 1000, backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            width: '100%', maxWidth: '450px', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
            backgroundColor: '#05070A', border: '2px solid #0B3D91',
            borderRadius: '8px', padding: '30px', position: 'relative', boxShadow: '0 0 30px rgba(11, 61, 145, 0.3)'
          }}>
            <button
              onClick={() => setShowCreate(false)}
              style={{ position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', cursor: 'pointer', color: '#555' }}
            >
              <X size={20} />
            </button>

            <h2 style={{ letterSpacing: '2px', textTransform: 'uppercase', color: '#0B3D91', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <UserPlus size={24} /> Añadir Personal
            </h2>

            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#555', marginBottom: '5px' }}>FULL NAME</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  style={{ width: '100%', padding: '10px', background: '#111', border: '1px solid #333', color: 'white' }}
                  required
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#555', marginBottom: '5px' }}>EMAIL CHANNEL</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  style={{ width: '100%', padding: '10px', background: '#111', border: '1px solid #333', color: 'white' }}
                  required
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#555', marginBottom: '5px' }}>SECURITY ROLE</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  style={{ width: '100%', padding: '10px', background: '#111', border: '1px solid #333', color: 'white' }}
                >
                  <option value="user">USER (Standard Access)</option>
                  <option value="admin">ADMIN (Central Command)</option>
                </select>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#555', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Key size={12} /> PASSWORD
                </label>
                <input
                  type="password"
                  placeholder="Contraseña inicial"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  style={{ width: '100%', padding: '10px', background: '#111', border: '1px solid #333', color: 'white' }}
                  required
                />
              </div>

              <button
                type="submit"
                className="nasa-button"
                disabled={creating}
                style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
              >
                <UserPlus size={16} /> {creating ? 'ENLISTING...' : 'CREAR PERSONAL'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
